import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, Circle, Plus, Trash2, Calendar } from "lucide-react";

interface Subject {
  id: string;
  name: string;
}

interface StudentSubject {
  id: string;
  subject_id: string;
  status: string;
  start_date: string | null;
  completion_date: string | null;
  exam_completed: boolean;
  exam_completion_date: string | null;
  exam_result: string | null;
  exam_fail_date: string | null;
  subjects: {
    name: string;
  };
}

interface Props {
  studentId: string;
}

const StudentSubjects = ({ studentId }: Props) => {
  const [studentSubjects, setStudentSubjects] = useState<StudentSubject[]>([]);
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [startDate, setStartDate] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch student's subjects
      const { data: studentSubjectsData, error: subjectsError } = await supabase
        .from("student_subjects")
        .select(`
          id,
          subject_id,
          status,
          start_date,
          completion_date,
          exam_completed,
          exam_completion_date,
          exam_result,
          exam_fail_date,
          subjects (
            name
          )
        `)
        .eq("student_id", studentId);

      if (subjectsError) throw subjectsError;
      setStudentSubjects(studentSubjectsData || []);

      // Fetch all available subjects
      const { data: allSubjectsData, error: allSubjectsError } = await supabase
        .from("subjects")
        .select("id, name")
        .order("name");

      if (allSubjectsError) throw allSubjectsError;
      setAllSubjects(allSubjectsData || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      toast.error("Failed to load subjects");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubject = async () => {
    if (!selectedSubject) {
      toast.error("Please select a subject");
      return;
    }

    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }

    try {
      const { error } = await supabase.from("student_subjects").insert({
        student_id: studentId,
        subject_id: selectedSubject,
        status: "Ongoing",
        start_date: startDate,
      });

      if (error) throw error;
      toast.success("Subject added successfully");
      setSelectedSubject("");
      setStartDate("");
      setShowAddSubject(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Failed to add subject");
    }
  };

  const handleDeleteSubject = async (subjectId: string) => {
    if (!window.confirm("Are you sure you want to delete this subject? This action cannot be undone.")) return;

    try {
      const { error } = await supabase
        .from("student_subjects")
        .delete()
        .eq("id", subjectId);

      if (error) throw error;
      toast.success("Subject deleted successfully");
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete subject");
    }
  };

  const handleMarkComplete = async (subjectId: string, currentStatus: string) => {
    const newStatus = currentStatus === "Completed" ? "Ongoing" : "Completed";
    const completionDate = newStatus === "Completed" ? new Date().toISOString().split('T')[0] : null;

    try {
      const { error } = await supabase
        .from("student_subjects")
        .update({ 
          status: newStatus,
          completion_date: completionDate
        })
        .eq("id", subjectId);

      if (error) throw error;
      toast.success(`Subject marked as ${newStatus}`);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Failed to update subject");
    }
  };

  const handleExamResultChange = async (subjectId: string, result: string | null, failDate?: string) => {
    try {
      const updateData: any = {
        exam_result: result,
        exam_completed: result !== null,
        exam_completion_date: result !== null && result === 'Pass' ? new Date().toISOString().split('T')[0] : null,
      };
      
      if (result === 'Fail' && failDate) {
        updateData.exam_fail_date = failDate;
      } else if (result !== 'Fail') {
        updateData.exam_fail_date = null;
      }

      const { error } = await supabase
        .from("student_subjects")
        .update(updateData)
        .eq("id", subjectId);

      if (error) throw error;
      toast.success(result ? `Exam marked as ${result}` : "Exam status reset");
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Failed to update exam result");
    }
  };

  const getSubjectDuration = (startDate: string | null, completionDate: string | null) => {
    if (!startDate) return null;
    const end = completionDate ? new Date(completionDate) : new Date();
    const start = new Date(startDate);
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getReappearDate = (failDate: string | null) => {
    if (!failDate) return null;
    const fail = new Date(failDate);
    const reappear = new Date(fail.getTime() + (45 * 24 * 60 * 60 * 1000));
    return reappear;
  };

  const getDaysUntilReappear = (failDate: string | null) => {
    if (!failDate) return null;
    const reappearDate = getReappearDate(failDate);
    if (!reappearDate) return null;
    const today = new Date();
    const days = Math.floor((reappearDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const availableSubjects = allSubjects.filter(
    subject => !studentSubjects.some(ss => ss.subject_id === subject.id)
  );

  if (loading) {
    return <div className="text-center py-4">Loading subjects...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg sm:text-xl">Subjects & Exams</CardTitle>
          <Button
            size="sm"
            onClick={() => setShowAddSubject(!showAddSubject)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Subject
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showAddSubject && (
          <div className="space-y-3 p-4 border rounded-lg bg-accent/50">
            <div>
              <Label>Subject *</Label>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a subject" />
                </SelectTrigger>
                <SelectContent>
                  {availableSubjects.map((subject) => (
                    <SelectItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Select the date when this subject started for tracking progress
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddSubject}>Add Subject</Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddSubject(false);
                  setSelectedSubject("");
                  setStartDate("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {studentSubjects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No subjects assigned yet. Click "Add Subject" to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {studentSubjects.map((ss) => (
              <div
                key={ss.id}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors gap-3"
              >
                <div className="flex items-start gap-3 flex-1">
                  <button
                    onClick={() => handleMarkComplete(ss.id, ss.status)}
                    className="text-muted-foreground hover:text-primary transition-colors mt-1"
                    title="Toggle subject completion"
                  >
                    {ss.status === "Completed" ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Circle className="h-5 w-5" />
                    )}
                  </button>
                  <div className="flex-1">
                    <p className="font-medium">{ss.subjects?.name}</p>
                    <div className="flex flex-col gap-1 text-sm text-muted-foreground mt-1">
                      {ss.start_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>Started: {new Date(ss.start_date).toLocaleDateString()}</span>
                          {(() => {
                            const duration = getSubjectDuration(ss.start_date, ss.completion_date);
                            return duration !== null && (
                              <span className="text-xs bg-muted px-2 py-0.5 rounded">
                                {duration} {duration === 1 ? 'day' : 'days'}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                      {ss.completion_date && (
                        <p>Completed: {new Date(ss.completion_date).toLocaleDateString()}</p>
                      )}
                      {ss.exam_result && (
                        <p className={ss.exam_result === 'Pass' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          Exam: {ss.exam_result} {ss.exam_completion_date && `(${new Date(ss.exam_completion_date).toLocaleDateString()})`}
                        </p>
                      )}
                      {ss.exam_fail_date && ss.exam_result === 'Fail' && (
                        <div className="text-orange-600 font-medium">
                          <p>Failed: {new Date(ss.exam_fail_date).toLocaleDateString()}</p>
                          <p>Reappear: {getReappearDate(ss.exam_fail_date)?.toLocaleDateString()}</p>
                          {(() => {
                            const daysLeft = getDaysUntilReappear(ss.exam_fail_date);
                            return daysLeft !== null && daysLeft > 0 && (
                              <p className="text-xs">{daysLeft} days until eligible</p>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={ss.status === "Completed" ? "default" : "secondary"}>
                    {ss.status}
                  </Badge>
                  <Select 
                    value={ss.exam_result || "none"} 
                    onValueChange={(value) => {
                      if (value === "Fail") {
                        const today = new Date().toISOString().split('T')[0];
                        const failDate = prompt("Enter exam fail date (YYYY-MM-DD):", today);
                        if (failDate) {
                          handleExamResultChange(ss.id, value, failDate);
                        }
                      } else {
                        handleExamResultChange(ss.id, value === "none" ? null : value);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Exam Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Taken</SelectItem>
                      <SelectItem value="Pass">Pass ✓</SelectItem>
                      <SelectItem value="Fail">Fail ✗</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSubject(ss.id)}
                    className="h-8 w-8 p-0"
                    title="Delete subject"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StudentSubjects;